/**
 * contentValidators/fillInBreadthChecks.ts
 *
 * Q2Q-LEZEN S-LEZ-01 — Soft breadth check for `fill_in.acceptedAnswers`.
 *
 * Authors of `fill_in` seeds often supply only 2–3 accepted-answer variants,
 * which produces false negatives on didactically valid student input
 * (case differences, trailing units, whitespace variants, synonyms,
 * "ongeveer 150 ton" vs "150 ton", etc.). The TAA-LEZEN audit (Q2Q
 * batch 1) flagged this as a recurring pattern (DETAIL-005, CONCLUSIE-005)
 * and recommended a low-severity governance signal.
 *
 * Discipline (mirrors S2-01 / S-LEZ-04/05):
 *   - SOFT signal only — info / NEAR_LOW severity. Some fill_in questions
 *     are legitimately strict (single canonical answer), so this is a
 *     COACH-FOR-AUTHOR signal, never a blocker.
 *   - Only fires when acceptedAnswers exists and is below the floor; if
 *     the seed has no acceptedAnswers at all, that is a different,
 *     pre-existing schema-stage concern and we stay silent.
 *   - Floor is conservative (4) — the threshold the audit recommended.
 *   - Skips silently for non-fill_in question types.
 */

import type { ValidationIssue } from '../../issueContract'
import { info } from '../../issueContract'
import type { Question } from '../../../../content'
import { STAGE } from './shared'
import { SEVERITY } from '../../severityScale'

/** Minimum recommended number of accepted-answer variants per audit. */
const FILL_IN_BREADTH_FLOOR = 4

interface FillInData {
  acceptedAnswers?: unknown
}

export function validateFillInBreadth(question: Question): ValidationIssue[] {
  if (question.questionType !== 'fill_in') return []

  const data = (question as unknown as { data?: FillInData }).data
  if (!data || typeof data !== 'object') return []

  const accepted = data.acceptedAnswers
  if (!Array.isArray(accepted)) return [] // schema-stage handles missing/invalid shape

  // String-only variants count toward breadth; ignore other shapes.
  const stringVariants = accepted.filter(
    (v): v is string => typeof v === 'string' && v.trim().length > 0,
  )

  if (stringVariants.length === 0) return [] // empty array — out of scope here
  if (stringVariants.length >= FILL_IN_BREADTH_FLOOR) return []

  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  return [
    info(
      'content',
      STAGE,
      'CONTENT_FILL_IN_NARROW',
      `fill_in.acceptedAnswers has only ${stringVariants.length} variant(s); recommended floor is ${FILL_IN_BREADTH_FLOOR}.`,
      {
        field: 'data.acceptedAnswers',
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.NEAR_LOW,
        suggestion:
          'Add variant spellings (case, whitespace, unit suffix), synonyms, and softening qualifiers ("ongeveer", "ruim", "meer dan") so didactically valid student input is not penalised. Aim for ≥4 variants; legitimate single-canonical-answer cases can be left and the info-finding can be ignored.',
        metadata: {
          reason: 'fill_in_too_narrow',
          variantCount: stringVariants.length,
          floor: FILL_IN_BREADTH_FLOOR,
        },
      },
    ),
  ]
}
